import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { HiXMark, HiCube } from 'react-icons/hi2';
import { toast } from 'react-toastify';
import { getStorageUnitsByLocalId } from '../../../../../../../../../../crud/management_api/storage_units';
import { transferProductStock } from '../../../../../../../../../../crud/management_api/stock_management';
import { AxiosResponse } from 'axios';
import { ProductStock } from '../../../../../../types';
import './TransferStock.scss';

interface StorageUnit {
  storage_unit_id: number;
  name: string;
}

interface MovimientoStockProps {
  isOpen: boolean;
  onClose: () => void;
  stockItemId: string;
  storageid: number;
  reportedStock: number;
  storageUnitName: string;
  setProductStockData: React.Dispatch<React.SetStateAction<ProductStock[]>>;
}

const MovimientoStock: React.FC<MovimientoStockProps> = ({
  isOpen,
  onClose,
  stockItemId,
  storageid,
  reportedStock,
  storageUnitName,
  setProductStockData,
}) => {
  const [storageUnits, setStorageUnits] = useState<StorageUnit[]>([]);
  const [selectedStorageUnitId, setSelectedStorageUnitId] = useState<number | null>(null);
  const [moveQuantity, setMoveQuantity] = useState<string>('');
  const [moveAll, setMoveAll] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchStorageUnits = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') ?? '{}');
        const localId = user ? Number(user.local_id) : 0;

        if (!localId) {
          console.warn('Local ID no encontrado en localStorage');
          return;
        }

        const response = await getStorageUnitsByLocalId(localId);
        setStorageUnits(response.data);
      } catch (error) {
        console.error('Error al obtener las unidades de almacenamiento:', error);
        toast.error('Error al obtener las unidades de almacenamiento.');
      }
    };

    if (isOpen) {
      fetchStorageUnits();
      setSelectedStorageUnitId(null);
      setMoveQuantity('');
      setMoveAll(false);
    }
  }, [isOpen]);

  const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numValue = Number(value);

    if (value === '' || (numValue >= 0 && numValue <= reportedStock)) {
      setMoveQuantity(value);
      if (value !== reportedStock.toString()) {
        setMoveAll(false);
      }
    }
  };

  const handleMoveAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setMoveAll(checked);

    if (checked) {
      setMoveQuantity(reportedStock.toString());
    } else {
      setMoveQuantity('');
    }
  };

  const handleTransfer = async () => {
    const quantity = Number(moveQuantity);

    if (!selectedStorageUnitId || isNaN(quantity) || quantity <= 0) {
      toast.error('Debes seleccionar unidad de almacenamiento y una cantidad válida.');
      return;
    }

    const id = stockItemId;
    const storageID = storageid;

    const payload = {
      storage_unit_id_to: selectedStorageUnitId,
      storage_unit_id_from: storageID,
      transfer_quantity: quantity,
    };

    console.log('Payload (transferencia):', payload);
    setIsLoading(true);

    try {
      const response = await transferProductStock(id, payload) as AxiosResponse<ProductStock>;

      setProductStockData((prev) =>
        prev.map((product) =>
          product.product_id === id && product.storage_unit_id === storageID
            ? { ...product, reported_stock: product.reported_stock - quantity, system_stock: product.system_stock - quantity }
            : product
        )
      );

      setProductStockData((prev) => {
        const exists = prev.some(
          (product) => product.product_id === id && product.storage_unit_id === payload.storage_unit_id_to
        );

        if (exists) {
          return prev.map((product) =>
            product.product_id === id && product.storage_unit_id === payload.storage_unit_id_to
              ? { ...product, reported_stock: product.reported_stock + quantity, system_stock: product.system_stock + quantity }
              : product
          );
        } else {
          const newProductStock = response.data;
          return [newProductStock, ...prev];
        }
      });

      toast.success('Movimiento realizado con éxito!');
      onClose();
    } catch (error: any) {
      console.error('Error al realizar el movimiento de stock:', error);
      const backendMsg = error?.response?.data?.message;
      if (backendMsg === 'No hay suficiente stock para transferir.') {
        toast.error('Cantidad de elementos superior a la existente en el almacenamiento');
      } else {
        toast.error('Ocurrió un error al mover el stock.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered dialogClassName="mp-modal">
      <div className="mp-modal-header">
        <div className="form-modal-title-container">
          <span className="form-modal-title">Transferir Stock</span>
        </div>
        <button className="mp-modal-close" onClick={onClose}>
          <HiXMark size={20} />
        </button>
      </div>

      <div className="mp-modal-body">
        <div className="mp-summary-card">
          <div className="mp-summary-item">
            <span className="label">Almacén origen</span>
            <span className="value">{storageUnitName}</span>
          </div>
          <div className="mp-summary-item">
            <span className="label">Stock disponible</span>
            <span className="value">{reportedStock}</span>
          </div>
        </div>

        <div className="mp-form-group">
          <label className="mp-form-label">Cantidad a mover</label>
          <input
            type="number"
            className="mp-form-input"
            value={moveQuantity}
            onChange={handleQuantityChange}
            min="0"
            max={reportedStock}
            placeholder="Ingrese la cantidad"
            disabled={moveAll}
          />
        </div>

        <div className="mp-form-group">
          <label className="mp-form-checkbox">
            <input
              type="checkbox"
              checked={moveAll}
              onChange={handleMoveAllChange}
            />
            <span>Mover todo ({reportedStock} unidades)</span>
          </label>
        </div>

        <div className="mp-form-group">
          <label className="mp-form-label">Almacén destino</label>
          <select
            className="mp-form-select"
            value={selectedStorageUnitId || ''}
            onChange={(e) => setSelectedStorageUnitId(Number(e.target.value))}
          >
            <option value="">Seleccionar almacén...</option>
            {storageUnits
              .filter((s) => s.storage_unit_id !== storageid)
              .map((s) => (
                <option key={s.storage_unit_id} value={s.storage_unit_id}>
                  {s.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="mp-modal-footer">
        <button className="mp-modal-btn-cancel" onClick={onClose} disabled={isLoading}>
          Cancelar
        </button>
        <button
          className="mp-modal-btn-submit"
          onClick={handleTransfer}
          disabled={isLoading || !selectedStorageUnitId || moveQuantity.trim() === ''}
        >
          {isLoading ? 'Transfiriendo...' : 'Transferir'}
        </button>
      </div>
    </Modal>
  );
};

export default MovimientoStock;
